import React from 'react';
import { View } from 'react-native-web';
import CustomText from '../../../../components/atoms/CustomText';
import { colors, spacing } from '../../../../utils/styling';
import { CATEGORY_SECTIONS } from '../../../../utils/category_mappings';
import { useAccountQuizzes } from '../../../../lib/quizzes';
import { migrateDatabaseQuizResults } from '../../../../utils/categoryMigration';
import { formatQuizDate } from '../../../../utils/quizDate';

// 2026-08-20 (McKayla Loom "Nitrogen Layout Part 1" t=1:00 → F15, ClickUp 86bah7dqn):
// this tab previously rendered a Holdings table and an Asset Allocation
// breakdown built entirely from a hardcoded MOCK_CLIENT_DATA constant —
// invented symbols, dollar amounts and percentages shown to an advisor,
// unlabelled, about a real client (BUGS.md Bug 13). That was deleted on
// 2026-08-20 and replaced with a named placeholder. This file now fills that
// placeholder with the four sections she asked for:
//   "this overview ... should say, like, this is the personality, this is what
//    the personality top traits are ... Planning tips ... maybe like an
//    overview of their financial questions that they answer"
//
// ⛔ DATA SOURCE — read this before changing the hook below.
// Every field rendered here comes from the CLIENT'S OWN quiz, fetched by their
// Salesforce account id via useAccountQuizzes(sfAccount). It must NOT be
// sourced from useCurrentProfile()/useProfileQuizzes(), which return the
// LOGGED-IN ADVISOR's record — on a client profile that renders the advisor's
// own results under the client's name, which is the same class of defect as
// Bug 13 (wrong data presented as this client's, with nothing on screen saying
// so). PersonalityTab.tsx currently has exactly that bug; see the note there.
//
// Do NOT reintroduce sample numbers, or invent copy, to make this look
// populated. Where we have no data source, this file says so by name.

// Planning tips have NO data source in the product today. CATEGORY_SECTIONS
// carries Subtext / Overview / DominantTraits / Strengths / Weaknesses /
// GrowthOpportunities / BehavioralConcepts — advisor-facing "planning tips" is
// not among them, and McKayla owns that copy (plan Q4). Growth opportunities
// are shown instead, under their own true label, and the planning-tips slot is
// an explicit placeholder rather than a repurposed field wearing a new name.
const PLANNING_TIPS_PENDING =
  'Planning tips for this personality are not written yet — this section fills in when that copy lands.';

const EMPTY_SECTION = {
  Subtext: '',
  Overview: '',
  FamousExample: ['', ''],
  DominantTraits: [] as string[],
  Strengths: [] as string[],
  Weaknesses: [] as string[],
  GrowthOpportunities: [] as string[],
  BehavioralConcepts: [] as string[],
};

type ResultsType = Record<string, number>;

function getDominantCategory(results: ResultsType): string | null {
  if (!results || Object.entries(results).length === 0) return null;
  return Object.entries(results).reduce((a, b) => (a[1] > b[1] ? a : b))[0];
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.card}>
      <CustomText style={styles.sectionTitle}>{title}</CustomText>
      {children}
    </View>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <View style={styles.list}>
      {items.map((item, i) => (
        <View key={`${i}-${item.slice(0, 24)}`} style={styles.listItem}>
          <View style={styles.dot} />
          <CustomText style={styles.listText}>{item}</CustomText>
        </View>
      ))}
    </View>
  );
}

export default function OverviewTab({
  sfAccount,
  clientName,
}: {
  sfAccount?: string;
  clientName?: string;
}) {
  const { data: quizzes, isLoading, error } = useAccountQuizzes(sfAccount || '');

  if (!sfAccount) {
    return (
      <View style={styles.content}>
        <Section title="Client overview">
          <CustomText style={styles.body}>
            This client has no account id, so their assessment cannot be looked up.
          </CustomText>
        </Section>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.content}>
        <Section title="Client overview">
          <CustomText style={styles.body}>Loading this client's overview…</CustomText>
        </Section>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.content}>
        <Section title="Client overview">
          <CustomText style={styles.body}>
            We could not load this client's assessment. Please try again.
          </CustomText>
        </Section>
      </View>
    );
  }

  const list: any[] = Array.isArray(quizzes) ? quizzes : [];

  // Awaiting-response state. Deliberately says nothing about the client's
  // personality — we do not have one for them yet, and a neutral-looking
  // default here would read as a result.
  if (list.length === 0) {
    return (
      <View style={styles.content}>
        <Section title="Client overview">
          <CustomText style={styles.statusAwaiting}>Awaiting client response</CustomText>
          <CustomText style={styles.body}>
            {clientName ? `${clientName} has` : 'This client has'} not completed the Money
            Personality assessment yet. Send them the link from the Money Personality tab —
            their personality, top traits and background answers appear here once they finish.
          </CustomText>
        </Section>
      </View>
    );
  }

  const sorted = [...list].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  const activeQuiz = migrateDatabaseQuizResults(sorted[0]);

  const mainResults: ResultsType = activeQuiz?.results?.mainQuiz || {};
  const dominantCategory = getDominantCategory(mainResults);

  // NEVER index CATEGORY_SECTIONS directly (DECISIONS.md 2026-07-20, ClickUp
  // 86bb0gg67). Stored results outlive the content maps; a bare index on a
  // retired personality name threw and white-screened the whole app.
  const resolvedSection = dominantCategory ? CATEGORY_SECTIONS[dominantCategory] : undefined;
  if (dominantCategory && !resolvedSection) {
    console.warn(
      `[OverviewTab] personality "${dominantCategory}" has no content section; rendering empty content.`
    );
  }
  const section = resolvedSection ?? EMPTY_SECTION;

  // A quiz row can exist with no scored result (created but abandoned). That is
  // still "not taken" from the advisor's point of view.
  if (!dominantCategory) {
    return (
      <View style={styles.content}>
        <Section title="Client overview">
          <CustomText style={styles.statusAwaiting}>Assessment started, not finished</CustomText>
          <CustomText style={styles.body}>
            {clientName ? `${clientName} opened` : 'This client opened'} the assessment but has
            not completed it, so there is no result to show yet.
          </CustomText>
        </Section>
      </View>
    );
  }

  const dateTaken = formatQuizDate(activeQuiz?.created_at);
  const backgroundAnswers: Array<{ question: string; answer: string }> = Array.isArray(
    activeQuiz?.info
  )
    ? activeQuiz.info
    : [];

  return (
    <View style={styles.content}>
      <Section title="Money Personality">
        <CustomText style={styles.personalityName}>{dominantCategory}</CustomText>
        {!!section.Subtext && (
          <CustomText style={styles.personalitySubtext}>{section.Subtext}</CustomText>
        )}
        {dateTaken && (
          <CustomText style={styles.meta}>Assessment completed {dateTaken}</CustomText>
        )}
        {!!section.Overview && <CustomText style={styles.body}>{section.Overview}</CustomText>}
      </Section>

      {section.DominantTraits.length > 0 && (
        <Section title="Top traits">
          <BulletList items={section.DominantTraits} />
        </Section>
      )}

      <Section title="Planning tips">
        <CustomText style={styles.pending}>{PLANNING_TIPS_PENDING}</CustomText>
        {section.GrowthOpportunities.length > 0 && (
          <View style={styles.subBlock}>
            <CustomText style={styles.subTitle}>
              Growth opportunities for this personality
            </CustomText>
            <BulletList items={section.GrowthOpportunities} />
          </View>
        )}
      </Section>

      <Section title="Background questions">
        {backgroundAnswers.length > 0 ? (
          <View style={styles.list}>
            {backgroundAnswers.map((entry, i) => (
              <View key={`${i}-${entry.question}`} style={styles.qaRow}>
                <CustomText style={styles.question}>{entry.question}</CustomText>
                <CustomText style={styles.answer}>{entry.answer}</CustomText>
              </View>
            ))}
          </View>
        ) : (
          <CustomText style={styles.pending}>
            This client did not answer the background questions.
          </CustomText>
        )}
      </Section>
    </View>
  );
}

const styles = {
  content: {
    padding: spacing.xl,
    maxWidth: 1200,
    marginHorizontal: 'auto',
    width: '100%',
    gap: spacing.xl,
    display: 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,
  card: {
    backgroundColor: colors.white,
    borderRadius: 8,
    padding: spacing.xl,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.dark,
  },
  personalityName: {
    marginTop: spacing.sm,
    fontSize: 24,
    fontWeight: '700',
    color: colors.primary,
  },
  personalitySubtext: {
    marginTop: spacing.xs,
    fontSize: 15,
    color: colors.gray[700],
  },
  statusAwaiting: {
    marginTop: spacing.sm,
    fontSize: 16,
    fontWeight: '600',
    color: colors.dark,
  },
  meta: {
    marginTop: spacing.sm,
    fontSize: 13,
    color: colors.gray[500],
  },
  body: {
    marginTop: spacing.sm,
    fontSize: 14,
    color: colors.gray[700],
  },
  pending: {
    marginTop: spacing.sm,
    fontSize: 14,
    color: colors.gray[500],
  },
  subBlock: {
    marginTop: spacing.lg,
  },
  subTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.dark,
  },
  list: {
    marginTop: spacing.sm,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.gray[300],
    marginTop: 7,
  },
  listText: {
    flex: 1,
    fontSize: 14,
    color: colors.gray[700],
  },
  qaRow: {
    marginBottom: spacing.md,
  },
  question: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.gray[600],
  },
  answer: {
    marginTop: spacing.xs,
    fontSize: 14,
    color: colors.dark,
  },
} as const;
